import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { CustomerListItem, CustomerStatus } from '@oscom/shared';
import { ApiRequestError } from '../api/client';
import { listCustomers } from '../api/customers';
import { useSession } from '../auth/SessionProvider';
import { Layout } from '../components/Layout';
import { CUSTOMER_STATUS_LABELS } from './customerStatusLabels';

/**
 * Customer list.
 *
 * Structured like `DashboardPage`: load in `useEffect`, show a warning notice
 * on failure, and never invent placeholder rows - an empty result renders the
 * literal empty state text instead of sample data.
 */
export function CustomersPage(): ReactNode {
  const { session } = useSession();
  const [customers, setCustomers] = useState<CustomerListItem[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    listCustomers()
      .then((response) => {
        if (!cancelled) {
          setCustomers(response.customers);
        }
      })
      .catch((caught: unknown) => {
        if (cancelled) {
          return;
        }
        setError(
          caught instanceof ApiRequestError
            ? caught.message
            : 'Kunden konnten nicht geladen werden.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (session?.status !== 'authenticated') {
    return null;
  }
  const { me } = session;
  const canCreate = me.permissions.includes('customers:write');

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        <div className="card">
          <h1>Kunden</h1>
          {canCreate ? (
            <p>
              <Link className="button button--primary" to="/kunden/neu">
                Kunde anlegen
              </Link>
            </p>
          ) : null}

          {error ? (
            <p className="notice notice--warning">{error}</p>
          ) : customers === undefined ? (
            <p className="muted" role="status" aria-live="polite">
              Kunden werden geladen ...
            </p>
          ) : customers.length === 0 ? (
            <p className="muted">Noch kein Kunde angelegt.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Kundennummer</th>
                    <th scope="col">Status</th>
                    <th scope="col">Geaendert am</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id}>
                      <td>
                        <Link to={`/kunden/${encodeURIComponent(customer.id)}`}>
                          {customer.name}
                        </Link>
                      </td>
                      <td>{customer.customerNumber ?? ''}</td>
                      <td>{statusLabel(customer.status)}</td>
                      <td>{formatDate(customer.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

function statusLabel(status: CustomerStatus): string {
  return CUSTOMER_STATUS_LABELS[status];
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = parsed.getFullYear();
  return `${day}.${month}.${year}`;
}
